import { inject, Injectable } from "@angular/core";
import { UploadedFloorplan } from "@components/floorplan-upload-overlay/uploaded-floorplan";
import { DataUrlMapperService } from "@utils/data-url-mapper.service";
import { PdfContentType, PdfToImageService } from "@utils/pdf-to-image.service";
import { createUploadError, UploadErrorKind } from "@utils/upload-error-mapper";
import { firstValueFrom } from "rxjs";

export type FloorplanUploadProcessError = {
  file: File;
  error: unknown;
};

export type FloorplanUploadProcessResult = {
  uploaded: UploadedFloorplan[];
  errors: FloorplanUploadProcessError[];
};

@Injectable({
  providedIn: "root",
})
export class FloorplanUploadProcessorService {
  private dataUrlMapperService = inject(DataUrlMapperService);
  private pdfToImageService = inject(PdfToImageService);

  async processFiles(files: File[], maxPdfSizeBytes: number): Promise<FloorplanUploadProcessResult> {
    const uploaded: UploadedFloorplan[] = [];
    const errors: FloorplanUploadProcessError[] = [];
    let viewport = null;

    for (const file of files) {
      try {
        const result = await this.processFile(file, viewport, maxPdfSizeBytes);
        viewport = result.viewport;

        if (result.plan) {
          uploaded.push(result.plan);
        }
      } catch (error) {
        errors.push({ file, error });
      }
    }

    return { uploaded, errors };
  }

  private async processFile(file: File, viewport: any, maxPdfSizeBytes: number) {
    if (this.isPdfFile(file)) {
      return await this.handlePdfFile(file, viewport, maxPdfSizeBytes);
    }

    if (this.isSvgFile(file)) {
      const svgText = await file.text();
      return { plan: this.saveImgAsSvg(svgText, file.name), viewport };
    }

    if (this.isImageFile(file)) {
      const plan = await this.saveImgAsImage(file);
      return { plan, viewport };
    }

    throw createUploadError(UploadErrorKind.UNKNOWN, file.name);
  }

  private async handlePdfFile(file: File, viewport: any, maxPdfSizeBytes: number) {
    if (file.size > maxPdfSizeBytes) {
      throw createUploadError(UploadErrorKind.PDF_TOO_LARGE, file.name);
    }

    let pdfContentType: PdfContentType;
    try {
      pdfContentType = await firstValueFrom(this.pdfToImageService.analyzePdfPageContent(file));
    } catch {
      throw createUploadError(UploadErrorKind.INVALID_PDF, file.name);
    }

    if (pdfContentType === PdfContentType.VECTOR || pdfContentType === PdfContentType.MIXED) {
      if (!navigator.onLine) {
        throw createUploadError(UploadErrorKind.OFFLINE, file.name);
      }
      const base64img = await firstValueFrom(this.pdfToImageService.convertToPng(file));
      return { plan: this.saveImgAsPng(base64img, file.name), viewport };
    }

    const result = await this.savePDFAsImage(file, viewport);
    return { plan: result.plan, viewport: result.viewport };
  }

  private async savePDFAsImage(file: File, viewport: any) {
    let canvas;
    try {
      canvas = await this.pdfToImageService.convert(file, viewport);
    } catch {
      throw createUploadError(UploadErrorKind.INVALID_PDF, file.name);
    }
    if (!canvas) {
      throw createUploadError(UploadErrorKind.INVALID_PDF, file.name);
    }
    const plan = new UploadedFloorplan(file.name, canvas.toDataURL("image/jpeg"));
    return { plan, viewport };
  }

  private saveImgAsPng(data: string, fileName: string) {
    return new UploadedFloorplan(fileName, `data:image/png;base64,${data}`);
  }

  private saveImgAsSvg(svgText: string, fileName: string) {
    const resizedSvg = this.resizeSvg(svgText);
    const encoded = `data:image/svg+xml;base64,${btoa(
      Array.from(new TextEncoder().encode(resizedSvg))
        .map((byte) => String.fromCharCode(byte))
        .join(""),
    )}`;
    return new UploadedFloorplan(fileName, encoded);
  }

  private resizeSvg(text: string) {
    const TARGET_HEIGHT = 2000;

    if (!/viewBox="/i.test(text)) {
      const widthMatch = text.match(/width="([\d.]+)"/i);
      const heightMatch = text.match(/height="([\d.]+)"/i);

      if (widthMatch && heightMatch) {
        const width = parseFloat(widthMatch[1]);
        const height = parseFloat(heightMatch[1]);
        const viewBox = `viewBox="0 0 ${width} ${height}"`;
        text = text.replace(/<svg/i, `<svg ${viewBox}`);
      } else {
        text = text.replace(/<svg/i, `<svg viewBox="0 0 ${TARGET_HEIGHT} ${TARGET_HEIGHT}"`);
      }
    }

    text = text.replace(/width="[^"]*"/i, "").replace(/height="[^"]*"/i, "");

    if (!/preserveAspectRatio="/i.test(text)) {
      text = text.replace(/<svg/i, `<svg height="${TARGET_HEIGHT}" preserveAspectRatio="xMidYMid meet"`);
    } else {
      text = text.replace(/<svg/i, `<svg height="${TARGET_HEIGHT}"`);
    }

    return text;
  }

  private async saveImgAsImage(file: File) {
    try {
      const url = await firstValueFrom(this.dataUrlMapperService.fileToDataURL(file));
      const img = await this.getImage(url);
      const canvas = this.drawImageToCanvas(img);
      if (!canvas) {
        throw createUploadError(UploadErrorKind.INVALID_IMAGE, file.name);
      }
      return new UploadedFloorplan(file.name, canvas.toDataURL("image/jpeg"));
    } catch {
      throw createUploadError(UploadErrorKind.INVALID_IMAGE, file.name);
    }
  }

  private drawImageToCanvas(img: HTMLImageElement) {
    const w = img.width;
    const h = img.height;

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    if (!context) {
      return undefined;
    }

    canvas.width = w;
    canvas.height = h;
    context.drawImage(img, 0, 0, w, h, 0, 0, w, h);

    return canvas;
  }

  private async getImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
  }

  private isPdfFile(file: File) {
    return file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
  }

  private isSvgFile(file: File) {
    return file.type === "image/svg+xml" || file.type === "image/svg" || file.name.toLowerCase().endsWith(".svg");
  }

  private isImageFile(file: File) {
    if (file.type.startsWith("image/")) {
      return true;
    }
    const lower = file.name.toLowerCase();
    return lower.endsWith(".png") || lower.endsWith(".jpg") || lower.endsWith(".jpeg") || lower.endsWith(".gif");
  }
}
